import React, { useState, useCallback, useRef } from 'react';
import { Copy, Check, LogOut, ArrowRightLeft } from 'lucide-react';
import { BattlePlayerList } from './BattlePlayerList';
import type { BattleRoomState } from '../../../types/battleRush';

interface BattleLobbyProps {
  room: BattleRoomState;
  currentUserId: string;
  isHost: boolean;
  onStart: () => void | Promise<void>;
  onLeave: () => void;
  onToggleReady: (ready: boolean) => void;
  onJoinRoom?: (code: string) => void;
  error: string | null;
}

export const BattleLobby: React.FC<BattleLobbyProps> = ({
  room,
  currentUserId,
  isHost,
  onStart,
  onLeave,
  onToggleReady,
  onJoinRoom,
  error,
}) => {
  const [copied, setCopied] = useState(false);
  const [showJoinInput, setShowJoinInput] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  const joinInputRef = useRef<HTMLInputElement>(null);

  const handleCopyLink = useCallback(async () => {
    const link = `${window.location.origin}/trivia-rush/battle/${room.roomCode}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const textarea = document.createElement('textarea');
      textarea.value = link;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [room.roomCode]);

  const nonHostPlayers = room.players.filter((p) => p.userId !== room.hostUserId);
  const allReady = nonHostPlayers.length > 0 && nonHostPlayers.every((p) => p.isReady);
  const canStart = isHost && room.players.length >= 2 && allReady;
  const myPlayer = room.players.find((p) => p.userId === currentUserId);
  const amReady = myPlayer?.isReady ?? false;

  return (
    <div className="flex flex-col items-center justify-center min-h-[80vh] px-6 text-center">
      <h1
        className="text-3xl sm:text-4xl font-bold tracking-tight mb-2 animate-fade-up opacity-0"
        style={{ color: 'var(--rush-text-primary)', fontFamily: 'DM Serif Display, serif' }}
      >
        BATTLE MODE
      </h1>
      <p
        className="text-sm mb-6 animate-fade-up-delay opacity-0"
        style={{ color: 'var(--rush-text-muted)' }}
      >
        Share the code below to invite players
      </p>

      {/* Room Code */}
      <div
        className="flex items-center gap-3 rounded-xl px-6 py-3 mb-4"
        style={{
          backgroundColor: 'rgba(139, 92, 246, 0.1)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
        }}
      >
        <span
          className="text-3xl font-mono font-bold tracking-[0.3em]"
          style={{ color: 'var(--rush-text-primary)' }}
        >
          {room.roomCode}
        </span>
      </div>

      {/* Copy Link */}
      <button
        onClick={handleCopyLink}
        className="flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 active:scale-95 mb-8"
        style={{
          backgroundColor: copied ? 'rgba(34, 197, 94, 0.2)' : 'rgba(139, 92, 246, 0.15)',
          color: copied ? 'var(--rush-correct)' : 'var(--rush-text-primary)',
          border: `1px solid ${copied ? 'rgba(34, 197, 94, 0.3)' : 'rgba(139, 92, 246, 0.3)'}`,
        }}
      >
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        {copied ? 'Link Copied!' : 'Copy Invite Link'}
      </button>

      {/* Players */}
      <div className="w-full max-w-xs mb-8">
        <p
          className="text-xs uppercase tracking-wider mb-3 text-left"
          style={{ color: 'var(--rush-text-muted)' }}
        >
          Players ({room.players.length}/{room.maxPlayers})
        </p>
        <BattlePlayerList
          players={room.players}
          currentUserId={currentUserId}
          hostUserId={room.hostUserId}
          showReady
        />
      </div>

      {error && (
        <p className="text-sm mb-4" style={{ color: 'var(--rush-wrong)' }}>
          {error}
        </p>
      )}

      {/* Actions */}
      <div className="flex flex-col gap-3 w-full max-w-xs">
        {isHost && (
          <button
            onClick={async () => {
              setIsStarting(true);
              try { await onStart(); } finally { setIsStarting(false); }
            }}
            disabled={!canStart || isStarting}
            className="flex items-center justify-center gap-2 px-10 py-4 rounded-full text-lg font-bold transition-all duration-200 disabled:opacity-40 hover:scale-105 active:scale-95"
            style={{
              backgroundColor: 'var(--rush-purple-mid)',
              color: 'var(--rush-text-primary)',
              boxShadow: canStart && !isStarting ? 'var(--rush-shadow-glow)' : undefined,
            }}
          >
            {isStarting ? (
              <>
                <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                STARTING...
              </>
            ) : room.players.length < 2
              ? 'WAITING FOR PLAYERS...'
              : !allReady
                ? 'WAITING FOR READY...'
                : 'START BATTLE'}
          </button>
        )}

        {!isHost && (
          <button
            onClick={() => onToggleReady(!amReady)}
            className="px-10 py-4 rounded-full text-lg font-bold transition-all duration-200 hover:scale-105 active:scale-95"
            style={{
              backgroundColor: amReady ? 'rgba(34, 197, 94, 0.2)' : 'var(--rush-purple-mid)',
              color: amReady ? 'var(--rush-correct)' : 'var(--rush-text-primary)',
              border: amReady ? '1px solid rgba(34, 197, 94, 0.3)' : 'none',
              boxShadow: amReady ? undefined : 'var(--rush-shadow-glow)',
            }}
          >
            {amReady ? 'READY ✓' : 'READY UP'}
          </button>
        )}

        <button
          onClick={onLeave}
          className="flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-opacity hover:opacity-80"
          style={{ color: 'var(--rush-text-muted)' }}
        >
          <LogOut className="w-4 h-4" />
          Leave Room
        </button>

        {onJoinRoom && !showJoinInput && (
          <button
            onClick={() => {
              setShowJoinInput(true);
              setTimeout(() => joinInputRef.current?.focus(), 50);
            }}
            className="flex items-center justify-center gap-2 px-8 py-3 rounded-full text-sm font-bold transition-all duration-200 hover:scale-105 active:scale-95"
            style={{
              backgroundColor: 'rgba(139, 92, 246, 0.15)',
              color: 'var(--rush-text-primary)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
            }}
          >
            <ArrowRightLeft className="w-4 h-4" />
            JOIN ANOTHER ROOM
          </button>
        )}

        {onJoinRoom && showJoinInput && (
          <div className="flex items-center justify-center gap-2">
            <input
              ref={joinInputRef}
              type="text"
              maxLength={6}
              autoFocus
              placeholder="ENTER CODE"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && joinCode.trim()) {
                  onJoinRoom(joinCode.trim());
                }
              }}
              className="w-[7.5rem] px-3 py-2 rounded-full text-center text-sm font-bold font-mono tracking-widest outline-none"
              style={{
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                color: 'var(--rush-text-primary)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
              }}
            />
            <button
              onClick={() => {
                if (joinCode.trim()) onJoinRoom(joinCode.trim());
              }}
              disabled={!joinCode.trim()}
              className="px-5 py-2 rounded-full text-sm font-bold transition-all duration-200 disabled:opacity-30 hover:scale-105 active:scale-95"
              style={{
                backgroundColor: 'rgba(139, 92, 246, 0.15)',
                color: 'var(--rush-text-primary)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
              }}
            >
              GO
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
